import { useRef, useState } from 'react';
import { TabContent, TabPane } from 'reactstrap';
import { useQueryClient } from '@tanstack/react-query';
import { NSBadge, NSNavTabs, NSSlideout } from 'bricks';
import { INSNavItem } from 'bricks/NSNavTabs/NSNavTabs';
import useToast from 'hooks/useToast';
import { useAssignUser } from 'hooks/useAssignUser';
import { useUnassignUser } from 'hooks/useUnassignUser';
import { TimelineAssignee } from 'types/timeline';
import { useUpdateTimeline } from 'views/Tasks/Timeline/hooks/useUpdateTimeline';
import { TimelineDraft, TimelineItem, TimelineSection, UpdateTimelinePayload } from '../../types';
import TimelineSlideoutHeader from './TimelineSlideoutHeader/TimelineSlideoutHeader';
import TimelineSlideoutDetails from './TimelineSlideoutDetails/TimelineSlideoutDetails';
import TimelineSlideoutComments from './TimelineSlideoutComments/TimelineSlideoutComments';
import TimelineSlideoutAttachments from './TimelineSlideoutAttachments/TimelineSlideoutAttachments';
import { DETAILS_TAB, ATTACHMENTS_TAB, COMMENTS_TAB, DUE_DILLIGENCE_TIMELINE_SLIDEOUT_TABS } from 'views/DealDashboard/constants';


type TimelineSlideoutProps = {
    isOpen: boolean;
    timeline: TimelineItem | null;
    activityTab: string | null;
    onTabChange: (tab: string) => void;
    dealId: number;
    orgId: string;
    editMode: boolean;
    setEditMode: (editMode: boolean) => void;
    onClose: () => void;
    onSwitchTimeline: (id: string) => void;
    onDuplicate: (timeline: TimelineItem) => void;
    onRequestDelete: (timeline: TimelineItem) => void;
    onMoveToSection: (timeline: TimelineItem, sectionId: number | null) => void;
    onExpandSection: (sectionId: number) => void;
    sections: TimelineSection[];
    attachmentsCount: number;
};

const buildDraft = (timeline: TimelineItem): TimelineDraft => ({
    name: timeline.name,
    target: timeline.target,
    completed: timeline.completed,
    description: timeline.description,
    assignees: timeline.assignees ?? [],
    sectionId: timeline.sectionId ?? null,
});

export const TimelineSlideout = ({
    isOpen, timeline, activityTab, onTabChange, dealId, orgId, editMode, setEditMode, onClose, onDuplicate, onRequestDelete,
    onMoveToSection, onExpandSection, sections, attachmentsCount
}: TimelineSlideoutProps) => {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();

    const numericTimelineId = timeline ? Number(timeline.id) : -1;
    const { mutate: updateTimeline } = useUpdateTimeline(() => {});
    const { mutateAsync: assignUser } = useAssignUser({ orgId, dealId, timelineId: numericTimelineId });
    const { mutateAsync: unassignUser } = useUnassignUser({ orgId, dealId, timelineId: numericTimelineId });

    const [initializedTimelineId, setInitializedTimelineId] = useState<string | null>(null);
    const [draft, setDraft] = useState<TimelineDraft | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const draftAssigneeIdRef = useRef(-1);

    const activeTab = activityTab && DUE_DILLIGENCE_TIMELINE_SLIDEOUT_TABS.includes(activityTab) ? activityTab : DETAILS_TAB;

    const currentId = timeline?.id ?? null;
    if (currentId !== initializedTimelineId) {
        setInitializedTimelineId(currentId);
        setDraft(timeline ? buildDraft(timeline) : null);
    }

    if (!timeline || !draft) return null;

    const invalidateTimelines = () => queryClient.invalidateQueries({ queryKey: ['timelines', orgId, dealId] });

    const handleFieldChange = (patch: Partial<TimelineDraft>) =>
        setDraft(prev => (prev ? { ...prev, ...patch } : prev));

    const handleAssign = (userCorrelationId: string) => {
        setDraft((prev) => {
            if (!prev || prev.assignees.some(a => a.userCorrelationId === userCorrelationId)) {
                return prev;
            }

            const newAssignee: TimelineAssignee = {
                id: draftAssigneeIdRef.current,
                timelineId: numericTimelineId,
                userCorrelationId,
                createdAt: '',
                createdBy: '',
            };
            
            draftAssigneeIdRef.current -= 1;

            return { ...prev, assignees: [...prev.assignees, newAssignee] };
        });
    };

    const handleRemove = (userCorrelationId: string) =>
        setDraft(prev => (prev
            ? { ...prev, assignees: prev.assignees.filter(a => a.userCorrelationId !== userCorrelationId) }
            : prev));

    const handleCancel = () => {
        setDraft(buildDraft(timeline));
        setEditMode(false);
    };

    const buildChangedFields = (): UpdateTimelinePayload => {
        const changed: UpdateTimelinePayload = {};
        const trimmedName = draft.name.trim();
        if (trimmedName && trimmedName !== timeline.name) changed.name = trimmedName;
        if (draft.target !== timeline.target) changed.target = draft.target;
        if (draft.completed !== timeline.completed) changed.completed = draft.completed;
        if ((draft.description ?? '') !== (timeline.description ?? '')) changed.description = draft.description || null;
        if (draft.sectionId !== (timeline.sectionId ?? null)) changed.sectionId = draft.sectionId;
        return changed;
    };

    const getAssigneeDelta = () => {
        const originalIds = new Set((timeline.assignees ?? []).map(a => a.userCorrelationId));
        const draftIds = new Set(draft.assignees.map(a => a.userCorrelationId));
        return {
            toAdd: [...draftIds].filter(id => !originalIds.has(id)),
            toRemove: [...originalIds].filter(id => !draftIds.has(id)),
        };
    };

    const handleSave = async () => {
        if (isSaving) return;

        const changed = buildChangedFields();
        const { toAdd, toRemove } = getAssigneeDelta();

        setIsSaving(true);
        try {
            await Promise.all([
                ...toAdd.map(userCorrelationId => assignUser({ userCorrelationId })),
                ...toRemove.map(userCorrelationId => unassignUser(userCorrelationId)),
            ]);
        } catch {
            showError('Failed to update activity');
            invalidateTimelines();
            setIsSaving(false);
            return;
        }

        if (Object.keys(changed).length > 0) {
            updateTimeline(
                { orgId, dealId, timelineId: timeline.id, patchData: changed },
                {
                    onSuccess: () => {
                        invalidateTimelines();
                        // Expand the destination so the activity stays visible after it re-groups.
                        if (changed.sectionId != null) onExpandSection(changed.sectionId);
                        showSuccess('Activity updated');
                        setEditMode(false);
                    },
                    onError: () => showError('Failed to update activity'),
                    onSettled: () => setIsSaving(false),
                },
            );
        } else {
            invalidateTimelines();
            showSuccess('Activity updated');
            setEditMode(false);
            setIsSaving(false);
        }
    };

    const navItems: INSNavItem[] = [
        {
            id: 'timelineSlideoutDetailsTab',
            name: 'Details',
            isActive: activeTab === DETAILS_TAB,
            callback: () => onTabChange(DETAILS_TAB),
        },
        {
            id: 'timelineSlideoutCommentsTab',
            name: 'Comments',
            isActive: activeTab === COMMENTS_TAB,
            callback: () => onTabChange(COMMENTS_TAB),
        },
        {
            id: 'timelineSlideoutAttachmentsTab',
            name: 'Attachments',
            isActive: activeTab === ATTACHMENTS_TAB,
            callback: () => onTabChange(ATTACHMENTS_TAB),
            badge: <NSBadge color="primary-lighten" className="ml-1">{attachmentsCount}</NSBadge>,
        },
    ];

    return (
        <NSSlideout
            isOpen={isOpen}
            toggle={onClose}
            size="lg"
            contentClassName="TimelineSlideout"
            modalBodyClassName="p-0"
            customHeader={(
                <TimelineSlideoutHeader
                    name={timeline.name}
                    editMode={editMode}
                    isDetailsTab={activeTab === DETAILS_TAB}
                    isSaving={isSaving}
                    toggle={onClose}
                    onEdit={() => setEditMode(true)}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDuplicate={() => onDuplicate(timeline)}
                    onDelete={() => onRequestDelete(timeline)}
                    onMoveToSection={sectionId => onMoveToSection(timeline, sectionId)}
                    sections={sections}
                    currentSectionId={timeline.sectionId}
                />
            )}
        >
            <NSNavTabs navItems={navItems} />

            <TabContent activeTab={activeTab}>
                <TabPane tabId={DETAILS_TAB}>
                    <TimelineSlideoutDetails
                        key={timeline.id}
                        timeline={timeline}
                        editMode={editMode}
                        draft={draft}
                        orgId={orgId}
                        sections={sections}
                        onFieldChange={handleFieldChange}
                        onAssign={handleAssign}
                        onRemove={handleRemove}
                    />
                </TabPane>
                <TabPane tabId={COMMENTS_TAB}>
                    <TimelineSlideoutComments
                        key={timeline.id}
                        orgId={orgId}
                        dealId={dealId}
                        timelineId={numericTimelineId}
                        enabled={activeTab === COMMENTS_TAB}
                    />
                </TabPane>
                <TabPane tabId={ATTACHMENTS_TAB}>
                    <TimelineSlideoutAttachments
                        timelineId={numericTimelineId}
                        dealId={dealId}
                        onAttachmentCountChange={() => invalidateTimelines()}
                    />
                </TabPane>
            </TabContent>
        </NSSlideout>
    );
};

export default TimelineSlideout;
